/**
 * MapItem - Crafteable in-game map system.
 *
 * When a player holds a MAP item in the hotbar, a small canvas overlay
 * appears showing explored terrain as colored pixels. Each map covers
 * a fixed 128x128 block area centered on where it was first activated.
 *
 * Controls:
 *   - Hold MAP in hotbar  -> small overlay (128x128 px) in bottom-right
 *   - Press N while holding MAP -> toggle full-screen view (256x256 px centered)
 *
 * Map data is stored per-map via a unique mapId attached to the inventory slot.
 */

import { ItemId } from './ItemRegistry.js';

// ============================================================
// CONSTANTS
// ============================================================

const MAP_BLOCKS = 128;           // area covered: 128x128 blocks
const SMALL_SIZE = 128;           // small overlay in pixels
const FULL_SIZE  = 256;           // fullscreen overlay in pixels
const UPDATE_INTERVAL = 2.0;      // seconds between partial scans
const SCAN_RADIUS = 24;           // blocks to re-scan each update cycle
const BORDER_WIDTH = 4;           // brown border width in pixels

// Block color lookup for map rendering
const MAP_BLOCK_COLORS = {
  0: null,              // AIR
  1: '#4CAF50',         // GRASS
  2: '#8B6B3E',         // DIRT
  3: '#888888',         // STONE
  4: '#F5DEB3',         // SAND
  5: '#6B4226',         // WOOD
  6: '#2D7B1E',         // LEAVES
  7: '#2196F3',         // WATER
  8: '#333333',         // BEDROCK
  9: '#FFFFFF',         // SNOW_GRASS
  10: '#C4AA6A',        // SANDSTONE
  11: '#2D6B2D',        // CACTUS
  12: '#B89050',        // PLANKS
  13: '#777777',        // COBBLESTONE
  14: '#AADDFF',        // GLASS
  15: '#D4AA30',        // WHEAT
  16: '#8B5E3C',        // DOOR_CLOSED
  17: '#8B5E3C',        // DOOR_OPEN
  18: '#6A6A6A',        // COAL_ORE
  19: '#8A8A7A',        // IRON_ORE
  20: '#8A8A5A',        // GOLD_ORE
  21: '#7A9A9A',        // DIAMOND_ORE
  22: '#8A6A6A',        // REDSTONE_ORE
  23: '#6A6A8A',        // LAPIS_ORE
  24: '#6A8A6A',        // EMERALD_ORE
  25: '#1A0A2E',        // OBSIDIAN
  26: '#FF8800',        // TORCH
  27: '#B89050',        // CRAFTING_TABLE
  28: '#7A7A7A',        // FURNACE
  29: '#8A6A5A',        // FURNACE_LIT
  30: '#9C7C4E',        // CHEST
  31: '#CC2200',        // TNT
  32: '#8B6B3E',        // BOOKSHELF
  33: '#AA5533',        // BRICKS
  34: '#D8D8D8',        // IRON_BLOCK
  35: '#E8C840',        // GOLD_BLOCK
  36: '#5AE8D4',        // DIAMOND_BLOCK
  37: '#8A8A8A',        // SLAB_STONE
  38: '#B89050',        // SLAB_WOOD
  39: '#B89050',        // FENCE
  40: '#8B6B3E',        // LADDER
  41: '#DD2222',        // FLOWER_RED
  42: '#FFDD00',        // FLOWER_YELLOW
  43: '#4A9A30',        // TALL_GRASS
  44: '#DD2222',        // MUSHROOM_RED
  45: '#8B6B3E',        // MUSHROOM_BROWN
  46: '#888877',        // GRAVEL
  47: '#A0A0AA',        // CLAY
  48: '#E8E0D0',        // LOG_BIRCH
  49: '#5AAA30',        // LEAVES_BIRCH
  50: '#F0F0F0',        // SNOW
  51: '#BBDDFF',        // ICE
  52: '#DD8820',        // PUMPKIN
  53: '#8AAA30',        // MELON
  54: '#F0F0F0',        // WOOL_WHITE
  55: '#668866',        // MOSS_STONE
  56: '#882222',        // NETHERRACK
  57: '#CCAA44',        // GLOWSTONE
  58: '#554433',        // SOUL_SAND
  59: '#442233',        // NETHER_BRICKS
  60: '#DDDDAA',        // END_STONE
  61: '#CC3333',        // BED
  62: '#5A3A1A',        // FARMLAND
  63: '#3A2A0A',        // FARMLAND_WET
  64: '#5A3A1A',        // JUNGLE_LOG
  65: '#1A5A1A',        // JUNGLE_LEAVES
  66: '#8B6B3E',        // ACACIA_LOG
  67: '#5A8B2E',        // ACACIA_LEAVES
  68: '#2A5A2A',        // VINE
  69: '#2A8A2A',        // LILY_PAD
};

// ============================================================
// MAP DATA STORE
// ============================================================

/** Global store keyed by mapId. Each entry holds the pixel data for one map. */
const mapDataStore = new Map();

let nextMapId = 1;

/** Assign a fresh mapId to a map inventory slot if it doesn't have one yet. */
function ensureMapId(slot) {
  if (!slot._mapId) {
    slot._mapId = nextMapId++;
  }
  return slot._mapId;
}

/** Get or create map pixel data for a given mapId + center. */
function getMapData(mapId, centerX, centerZ) {
  if (!mapDataStore.has(mapId)) {
    mapDataStore.set(mapId, {
      centerX,
      centerZ,
      pixels: new Uint8Array(MAP_BLOCKS * MAP_BLOCKS * 3), // RGB per pixel
      scanned: new Uint8Array(MAP_BLOCKS * MAP_BLOCKS),     // 1 if scanned
    });
  }
  return mapDataStore.get(mapId);
}

// ============================================================
// MAP ITEM CLASS
// ============================================================

export class MapItem {
  /**
   * @param {Object} world - World instance (needs getTopBlock, getBlock, isSolid)
   */
  constructor(world) {
    this.world = world;
    this._updateTimer = 0;
    this._fullView = false;
    this._visible = false;
    this._currentMapId = null;

    // --- Off-screen canvas for map rendering ---
    this._mapCanvas = document.createElement('canvas');
    this._mapCanvas.width = MAP_BLOCKS;
    this._mapCanvas.height = MAP_BLOCKS;
    this._mapCtx = this._mapCanvas.getContext('2d');

    // --- Overlay canvas (displayed on screen) ---
    this.canvas = document.createElement('canvas');
    this.canvas.width = SMALL_SIZE;
    this.canvas.height = SMALL_SIZE;
    this.canvas.style.cssText = `
      position: fixed;
      bottom: 60px;
      right: 10px;
      width: ${SMALL_SIZE}px;
      height: ${SMALL_SIZE}px;
      image-rendering: pixelated;
      z-index: 99;
      display: none;
      pointer-events: none;
    `;
    document.body.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d');

    // --- Border container ---
    this._border = document.createElement('div');
    this._border.style.cssText = `
      position: fixed;
      bottom: ${60 - BORDER_WIDTH}px;
      right: ${10 - BORDER_WIDTH}px;
      width: ${SMALL_SIZE + BORDER_WIDTH * 2}px;
      height: ${SMALL_SIZE + BORDER_WIDTH * 2}px;
      border: ${BORDER_WIDTH}px solid #8B6B3E;
      box-shadow: inset 0 0 0 1px rgba(0,0,0,0.4), 0 0 6px rgba(0,0,0,0.5);
      z-index: 98;
      display: none;
      pointer-events: none;
      box-sizing: border-box;
    `;
    document.body.appendChild(this._border);

    // --- "Mapa" label ---
    this._label = document.createElement('div');
    this._label.textContent = 'Mapa';
    this._label.style.cssText = `
      position: fixed;
      bottom: ${60 + SMALL_SIZE + 2}px;
      right: 10px;
      color: #FFF;
      font-family: 'Courier New', monospace;
      font-size: 11px;
      text-shadow: 1px 1px 0 #3F3F3F;
      z-index: 99;
      display: none;
      pointer-events: none;
    `;
    document.body.appendChild(this._label);
  }

  /**
   * Toggle full-screen map view. Called when player presses N while holding a map.
   */
  toggleFullView() {
    if (!this._visible) return;
    this._fullView = !this._fullView;
    this._applyLayout();
  }

  /**
   * Main update loop. Called every frame from the game loop.
   *
   * @param {number} dt - delta time in seconds
   * @param {Object} playerPos - { x, y, z }
   * @param {number} playerYaw - camera yaw in radians
   * @param {Object} inventory - player inventory (needs getSelectedSlot())
   */
  update(dt, playerPos, playerYaw, inventory) {
    const slot = inventory.getSelectedSlot();
    const holdingMap = slot && slot.itemId === ItemId.MAP && slot.count > 0;

    if (holdingMap) {
      if (!this._visible) {
        this._visible = true;
        this._fullView = false;
        this._applyLayout();
      }

      const mapId = ensureMapId(slot);
      this._currentMapId = mapId;

      // Initialize center on first use
      const px = Math.floor(playerPos.x);
      const pz = Math.floor(playerPos.z);
      const data = getMapData(mapId, px, pz);

      // Periodic scan
      this._updateTimer += dt;
      if (this._updateTimer >= UPDATE_INTERVAL) {
        this._updateTimer = 0;
        this._scanArea(data, px, pz);
      }

      // Render map
      this._render(data, px, pz, playerYaw);
    } else {
      if (this._visible) {
        this._visible = false;
        this._fullView = false;
        this._hide();
      }
    }
  }

  // ============================================================
  // PRIVATE
  // ============================================================

  /**
   * Scan terrain around the player and write into map pixel data.
   */
  _scanArea(data, playerX, playerZ) {
    const halfMap = MAP_BLOCKS / 2;
    const originX = data.centerX - halfMap;
    const originZ = data.centerZ - halfMap;

    // Scan a square around the player
    const scanMinX = playerX - SCAN_RADIUS;
    const scanMaxX = playerX + SCAN_RADIUS;
    const scanMinZ = playerZ - SCAN_RADIUS;
    const scanMaxZ = playerZ + SCAN_RADIUS;

    for (let wz = scanMinZ; wz <= scanMaxZ; wz++) {
      for (let wx = scanMinX; wx <= scanMaxX; wx++) {
        const px = wx - originX;
        const pz = wz - originZ;

        // Out of map bounds
        if (px < 0 || px >= MAP_BLOCKS || pz < 0 || pz >= MAP_BLOCKS) continue;

        const topY = this.world.getTopBlock(wx, wz);
        if (topY <= 0) continue;

        const blockId = this.world.getBlock(wx, topY, wz);
        const colorHex = MAP_BLOCK_COLORS[blockId];
        if (!colorHex) continue;

        // Parse hex color
        const r = parseInt(colorHex.slice(1, 3), 16);
        const g = parseInt(colorHex.slice(3, 5), 16);
        const b = parseInt(colorHex.slice(5, 7), 16);

        // Apply subtle height shading
        const shade = Math.max(0.6, Math.min(1.2, topY / 50));

        const idx = (pz * MAP_BLOCKS + px) * 3;
        data.pixels[idx]     = Math.min(255, Math.floor(r * shade));
        data.pixels[idx + 1] = Math.min(255, Math.floor(g * shade));
        data.pixels[idx + 2] = Math.min(255, Math.floor(b * shade));
        data.scanned[pz * MAP_BLOCKS + px] = 1;
      }
    }
  }

  /**
   * Render the map canvas from pixel data.
   */
  _render(data, playerX, playerZ, playerYaw) {
    const mCtx = this._mapCtx;
    const mapCanvas = this._mapCanvas;

    // Create ImageData from pixel array
    const imgData = mCtx.createImageData(MAP_BLOCKS, MAP_BLOCKS);
    const d = imgData.data;

    for (let i = 0; i < MAP_BLOCKS * MAP_BLOCKS; i++) {
      const si = i * 3;
      const di = i * 4;
      if (data.scanned[i]) {
        d[di]     = data.pixels[si];
        d[di + 1] = data.pixels[si + 1];
        d[di + 2] = data.pixels[si + 2];
        d[di + 3] = 255;
      } else {
        // Unexplored area: dark tan (parchment)
        d[di]     = 180;
        d[di + 1] = 160;
        d[di + 2] = 130;
        d[di + 3] = 255;
      }
    }

    mCtx.putImageData(imgData, 0, 0);

    // Draw player marker on map canvas
    const halfMap = MAP_BLOCKS / 2;
    const markerX = playerX - (data.centerX - halfMap);
    const markerZ = playerZ - (data.centerZ - halfMap);

    if (markerX >= 0 && markerX < MAP_BLOCKS && markerZ >= 0 && markerZ < MAP_BLOCKS) {
      mCtx.save();
      mCtx.translate(markerX, markerZ);
      mCtx.rotate(-playerYaw);

      // White arrow
      mCtx.fillStyle = '#FFFFFF';
      mCtx.beginPath();
      mCtx.moveTo(0, -3);
      mCtx.lineTo(-2, 2);
      mCtx.lineTo(0, 1);
      mCtx.lineTo(2, 2);
      mCtx.closePath();
      mCtx.fill();

      mCtx.strokeStyle = '#000';
      mCtx.lineWidth = 0.5;
      mCtx.stroke();

      mCtx.restore();
    }

    // Draw to overlay canvas (scaled)
    const ctx = this.ctx;
    const size = this._fullView ? FULL_SIZE : SMALL_SIZE;
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, size, size);
    ctx.drawImage(mapCanvas, 0, 0, size, size);
  }

  /**
   * Apply the current layout (small or full) to the DOM elements.
   */
  _applyLayout() {
    const size = this._fullView ? FULL_SIZE : SMALL_SIZE;

    this.canvas.width = size;
    this.canvas.height = size;

    if (this._fullView) {
      // Centered on screen
      this.canvas.style.bottom = '';
      this.canvas.style.right = '';
      this.canvas.style.top = '50%';
      this.canvas.style.left = '50%';
      this.canvas.style.transform = 'translate(-50%, -50%)';
      this.canvas.style.width = `${size}px`;
      this.canvas.style.height = `${size}px`;

      this._border.style.bottom = '';
      this._border.style.right = '';
      this._border.style.top = '50%';
      this._border.style.left = '50%';
      this._border.style.transform = 'translate(-50%, -50%)';
      this._border.style.width = `${size + BORDER_WIDTH * 2}px`;
      this._border.style.height = `${size + BORDER_WIDTH * 2}px`;

      this._label.style.bottom = '';
      this._label.style.right = '';
      this._label.style.top = `calc(50% - ${size / 2 + 18}px)`;
      this._label.style.left = '50%';
      this._label.style.transform = 'translateX(-50%)';
      this._label.style.fontSize = '14px';
    } else {
      // Bottom-right corner
      this.canvas.style.top = '';
      this.canvas.style.left = '';
      this.canvas.style.transform = '';
      this.canvas.style.bottom = '60px';
      this.canvas.style.right = '10px';
      this.canvas.style.width = `${size}px`;
      this.canvas.style.height = `${size}px`;

      this._border.style.top = '';
      this._border.style.left = '';
      this._border.style.transform = '';
      this._border.style.bottom = `${60 - BORDER_WIDTH}px`;
      this._border.style.right = `${10 - BORDER_WIDTH}px`;
      this._border.style.width = `${size + BORDER_WIDTH * 2}px`;
      this._border.style.height = `${size + BORDER_WIDTH * 2}px`;

      this._label.style.top = '';
      this._label.style.left = '';
      this._label.style.transform = '';
      this._label.style.bottom = `${60 + size + 2}px`;
      this._label.style.right = '10px';
      this._label.style.fontSize = '11px';
    }

    this.canvas.style.display = 'block';
    this._border.style.display = 'block';
    this._label.style.display = 'block';
  }

  /**
   * Hide all map DOM elements.
   */
  _hide() {
    this.canvas.style.display = 'none';
    this._border.style.display = 'none';
    this._label.style.display = 'none';
  }

  /**
   * Clean up DOM elements (call on game shutdown if needed).
   */
  dispose() {
    this._hide();
    this.canvas.remove();
    this._border.remove();
    this._label.remove();
  }
}
